import { FocusEvent, KeyboardEvent, useEffect, useRef, useState } from "react"
import { Tooltip } from "antd"
import { ChevronLeftIcon } from "components/ui/customWebviewer/JumpingNavigationBar/icons/ChevronLeftIcon"
import { ChevronRightIcon } from "components/ui/customWebviewer/JumpingNavigationBar/icons/ChevronRightIcon"
import { defaultTooltipStyles } from "components/ui/customWebviewer/shared/shadowTooltip"
import style from "./PageNavigationBar.module.scss"

const {
	wrapper,
	pill,
	navPill,
	namePill,
	iconButton,
	divider,
	counter,
	input,
	nameInput,
	inputError,
	totalLabel,
	pageLabel,
	nameInputWrapper,
} = style

type Props = {
	currentPage: number
	totalPages: number
	pageName: string | null
	onPageChange: (page: number) => void
	onPageNameNavigate: (name: string) => boolean
	onFocusChange?: (focused: boolean) => void
}

export const PageNavigationBar = ({
	currentPage,
	totalPages,
	pageName,
	onPageChange,
	onPageNameNavigate,
	onFocusChange,
}: Props) => {
	const [pageInputValue, setPageInputValue] = useState(String(currentPage))
	const [nameInputValue, setNameInputValue] = useState(pageName ?? "")
	const [nameError, setNameError] = useState<string | null>(null)
	const errorTimerRef = useRef<number | null>(null)
	const wrapperRef = useRef<HTMLDivElement>(null)
	// Escape schedules a state revert and immediately blurs, but blur fires
	// synchronously with the stale closure value — so the commit handlers would
	// see the pre-revert value and "commit" it. These refs let Escape tell the
	// next blur to skip its commit pass.
	const skipPageCommitRef = useRef(false)
	const skipNameCommitRef = useRef(false)

	// Anchor the antd Tooltip popup to the bar's own wrapper. The wrapper is
	// mounted inside the Apryse shadow root, so the popup is guaranteed to live
	// in the same shadow tree (where antd styles are injected via StyleProvider).
	const getTooltipContainer = (trigger: HTMLElement) =>
		wrapperRef.current ?? trigger.parentElement ?? trigger

	useEffect(() => {
		setPageInputValue(String(currentPage))
	}, [currentPage])

	useEffect(() => {
		setNameInputValue(pageName ?? "")
		setNameError(null)
	}, [pageName])

	useEffect(() => {
		return () => {
			if (errorTimerRef.current != null) window.clearTimeout(errorTimerRef.current)
		}
	}, [])

	const prevDisabled = currentPage <= 1
	const nextDisabled = currentPage >= totalPages

	// Inputs grow with their content. Floor ensures a sensible minimum so a
	// single-digit page or short label still reads as an input, and never
	// shrink below what the max page number / current page name would need.
	const INPUT_PADDING_PX = 18 // 8px L + 8px R padding + 2px border
	const pageInputChars = Math.max(2, String(totalPages).length, pageInputValue.length)
	const pageInputWidth = `calc(${pageInputChars}ch + ${INPUT_PADDING_PX}px)`
	const nameInputChars = Math.max(4, (pageName ?? "").length, nameInputValue.length)
	const nameInputWidth = `calc(${nameInputChars}ch + ${INPUT_PADDING_PX}px)`

	const commitPageInput = () => {
		if (skipPageCommitRef.current) {
			skipPageCommitRef.current = false
			return
		}
		const trimmed = pageInputValue.trim()
		const parsed = Number.parseInt(trimmed, 10)
		if (!Number.isFinite(parsed) || parsed < 1 || parsed > totalPages) {
			setPageInputValue(String(currentPage))
			return
		}
		if (parsed !== currentPage) onPageChange(parsed)
		setPageInputValue(String(parsed))
	}

	const showNameError = (label: string) => {
		setNameError(label)
		if (errorTimerRef.current != null) window.clearTimeout(errorTimerRef.current)
		errorTimerRef.current = window.setTimeout(() => setNameError(null), 2500)
	}

	const commitNameInput = () => {
		if (skipNameCommitRef.current) {
			skipNameCommitRef.current = false
			return
		}
		const trimmed = nameInputValue.trim()
		if (trimmed === "" || trimmed === (pageName ?? "")) {
			setNameInputValue(pageName ?? "")
			setNameError(null)
			return
		}
		const ok = onPageNameNavigate(trimmed)
		if (!ok) {
			showNameError(trimmed)
			setNameInputValue(pageName ?? "")
		}
	}

	const handlePageKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault()
			;(event.target as HTMLInputElement).blur()
		} else if (event.key === "Escape") {
			event.preventDefault()
			skipPageCommitRef.current = true
			setPageInputValue(String(currentPage))
			;(event.target as HTMLInputElement).blur()
		}
	}

	const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			event.preventDefault()
			;(event.target as HTMLInputElement).blur()
		} else if (event.key === "Escape") {
			event.preventDefault()
			skipNameCommitRef.current = true
			setNameInputValue(pageName ?? "")
			setNameError(null)
			;(event.target as HTMLInputElement).blur()
		}
	}

	const handleFocus = () => onFocusChange?.(true)
	const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
		// Only notify when focus actually leaves the bar, not when it moves between inputs.
		if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
		onFocusChange?.(false)
	}

	return (
		<div
			ref={wrapperRef}
			className={wrapper}
			role="toolbar"
			aria-label="Page navigation"
			onFocus={handleFocus}
			onBlur={handleBlur}
		>
			<div className={`${pill} ${navPill}`}>
				<button
					type="button"
					className={iconButton}
					onClick={() => !prevDisabled && onPageChange(currentPage - 1)}
					disabled={prevDisabled}
					aria-label="Previous page"
				>
					<ChevronLeftIcon />
				</button>
				<span className={divider} aria-hidden="true" />
				<div className={counter}>
					<input
						type="text"
						inputMode="numeric"
						className={input}
						style={{ width: pageInputWidth }}
						value={pageInputValue}
						onChange={(e) => setPageInputValue(e.target.value.replace(/[^0-9]/g, ""))}
						onBlur={commitPageInput}
						onKeyDown={handlePageKeyDown}
						onFocus={(e) => e.target.select()}
						aria-label="Current page"
					/>
					<span className={totalLabel}>{totalPages}</span>
				</div>
				<span className={divider} aria-hidden="true" />
				<button
					type="button"
					className={iconButton}
					onClick={() => !nextDisabled && onPageChange(currentPage + 1)}
					disabled={nextDisabled}
					aria-label="Next page"
				>
					<ChevronRightIcon />
				</button>
			</div>
			<div className={`${pill} ${namePill}`}>
				<span className={pageLabel}>Page</span>
				<span className={nameInputWrapper}>
					<Tooltip
						title={nameError != null ? `No page with label ${nameError}` : ""}
						open={nameError != null}
						placement="top"
						getPopupContainer={getTooltipContainer}
						styles={defaultTooltipStyles}
					>
						<input
							type="text"
							className={`${input} ${nameInput} ${nameError != null ? inputError : ""}`}
							style={{ width: nameInputWidth }}
							value={nameInputValue}
							onChange={(e) => {
								setNameInputValue(e.target.value)
								if (nameError != null) setNameError(null)
							}}
							onBlur={commitNameInput}
							onKeyDown={handleNameKeyDown}
							onFocus={(e) => e.target.select()}
							aria-label="Page name"
						/>
					</Tooltip>
				</span>
			</div>
		</div>
	)
}
